import { CalendarDate, getLocalTimeZone, parseAbsoluteToLocal } from '@internationalized/date';

import type { DateRange as AriaDateRange, DateValue } from '@react-aria/datepicker';

import type { DateRangeValue } from '../types/date.types';

/**
 * Converts a JavaScript Date object to `react-aria` compatible date string.
 *
 * @param date - The date to be converted.
 * @returns The converted react-aria `DateValue`, or null/undefined if the input date is null/undefined.
 */
export function convertDateToAriaDate(date: Date): DateValue;
export function convertDateToAriaDate(date: Date | null): DateValue | null;
export function convertDateToAriaDate(date: Date | undefined): DateValue | undefined;
export function convertDateToAriaDate(date: Date | null | undefined): DateValue | null | undefined;
export function convertDateToAriaDate(date: Date | null | undefined): DateValue | null | undefined {
  if (date === null || date === undefined) {
    return date;
  }

  const fullYear = date.getFullYear();
  const era = fullYear < 0 ? 'BC' : 'AD';
  const year = fullYear < 0 ? -fullYear : fullYear;
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return new CalendarDate(era, year, month, day);
}

/**
 * Converts a JavaScript Date object to `react-aria` compatible date time string.
 *
 * @param date - The date to be converted.
 * @returns The converted react-aria `DateValue`, or null/undefined if the input date is null/undefined.
 */
export function convertDateToAriaDateTime(date: Date): DateValue;
export function convertDateToAriaDateTime(date: Date | null): DateValue | null;
export function convertDateToAriaDateTime(date: Date | undefined): DateValue | undefined;
export function convertDateToAriaDateTime(date: Date | null | undefined): DateValue | null | undefined;
export function convertDateToAriaDateTime(date: Date | null | undefined): DateValue | null | undefined {
  if (date === null || date === undefined) {
    return date;
  }

  const parsedDate = parseAbsoluteToLocal(date.toISOString());
  return parsedDate;
}

/**
 * Converts a `react-aria` compatible date to a JavaScript Date object.
 *
 * @param date - The date to be converted.
 * @returns The converted JavaScript Date, or null/undefined if the input date is null/undefined.
 */
export function convertAriaDateToDate(date: DateValue): Date;
export function convertAriaDateToDate(date: DateValue | undefined): Date | undefined;
export function convertAriaDateToDate(date: DateValue | null): Date | null;
export function convertAriaDateToDate(date: DateValue | null | undefined): Date | null | undefined;
export function convertAriaDateToDate(date: DateValue | null | undefined): Date | null | undefined {
  if (date === null || date === undefined) {
    return date;
  }

  return date.toDate(getLocalTimeZone());
}

export function convertDateRangeToAriaDateRange(range: DateRangeValue | null): AriaDateRange | null;
export function convertDateRangeToAriaDateRange(range: DateRangeValue | undefined): AriaDateRange | undefined;
export function convertDateRangeToAriaDateRange(
  range: DateRangeValue | null | undefined,
): AriaDateRange | null | undefined;
export function convertDateRangeToAriaDateRange(
  range: DateRangeValue | null | undefined,
): AriaDateRange | null | undefined {
  if (range === undefined || range === null) {
    return range;
  }

  return {
    end: convertDateToAriaDateTime(range.end),
    start: convertDateToAriaDateTime(range.start),
  };
}

export function convertAriaDateRangeToDateRange(range: DateRangeValue<DateValue>): DateRangeValue<Date>;
export function convertAriaDateRangeToDateRange(range: DateRangeValue<DateValue> | null): DateRangeValue<Date> | null;
export function convertAriaDateRangeToDateRange(
  range: DateRangeValue<DateValue> | undefined,
): DateRangeValue<Date> | undefined;
export function convertAriaDateRangeToDateRange(
  range: DateRangeValue<DateValue | null> | null,
): DateRangeValue<Date | null> | null;
export function convertAriaDateRangeToDateRange(
  range: DateRangeValue<DateValue | undefined> | undefined,
): DateRangeValue<Date | undefined> | undefined;
export function convertAriaDateRangeToDateRange(
  range: DateRangeValue<DateValue | null | undefined> | null | undefined,
): DateRangeValue<Date | null | undefined> | null | undefined {
  if (range === undefined || range === null) {
    return range;
  }

  return {
    end: convertAriaDateToDate(range.end),
    start: convertAriaDateToDate(range.start),
  };
}
